'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import type { ProductDisplayData as Product } from '@eliadmin/core/src/api/products/types/product-types';
import { AddToCartButton } from '@eliadmin/core/src/components/product/add-to-cart-button';
import { AddToFavoritesButton } from '@eliadmin/core/src/components/product/add-to-favorites-button';
import { ProductPrice } from '@eliadmin/core/src/components/product/product-price';
import { ProductStock } from '@eliadmin/core/src/components/product/product-stock';
import { Badge } from '@eliadmin/core/src/components/ui/badge';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Card, CardContent, CardFooter } from '@eliadmin/core/src/components/ui/card';
import { DialogProvider } from '@eliadmin/core/src/components/ui/dialog';
import { ResponsiveDialogProvider } from '@eliadmin/core/src/components/ui/responsive-dialog';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCategoryStore } from '@eliadmin/core/src/stores/category-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { VariantsProvider, useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import { buildImageSizes } from '@eliadmin/core/src/components/image/image-helpers';
import {
  getProductFilterVariants,
  getProductImage,
  getProductUrl,
  isSellable,
} from '@eliadmin/core/src/utils/product-utils';
import { cn, getContrastYIQ } from '@eliadmin/core/src/utils/style-utils';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';
import { Fragment, useState } from 'react';
import { PriceCalculatorButton } from '../../shared/buttons/price-calculator-button';
import { AskForPriceDialog } from '@eliadmin/core/src/components/product/dialog/ask-for-price-dialog';
import { HtmlRendererRaw } from '@eliadmin/core/src/components/ui/html-renderer-raw';
import { getProductPriceDisplay } from '@eliadmin/core/src/utils/product-price-utils';

export type ProductCardProps = {
  product: Product;
  className?: {
    main?: string;
  };
};

function ProductCardInternal({ product, className }: ProductCardProps) {
  //   const isAuth = useGlobalStore(state => state.config.isAuth);
  const { t } = useTranslations();
  const templateSettings = useGlobalStore(s => s.templateSettings);
  // const api = useGlobalStore(state => state.config.api);
  const filter = useCategoryStore(state => state.filter);
  const selectedValues = useVariantsStore(state => state.selectedValues);

  const filterVariants = getProductFilterVariants(product, filter);
  const image = getProductImage(templateSettings, product, filterVariants);
  const imageUrl = image?.url?.trim();
  const productUrl = getProductUrl(product, selectedValues);
  const [isOpenAskForPriceDialog, setIsOpenAskForPriceDialog] = useState(false);

  const isCistiarne = product?.categories?.some(category => category?.id?.toString() === '1');
  const priceDisplay = getProductPriceDisplay(product);
  const askForPrice = isCistiarne || priceDisplay.priceOnRequest;

  return (
    <Card
      className={cn(
        // shadow-[0_-2px_60px_-15px_rgba(0,0,0,0.15)] removed, kept it there because of nice regex pattern
        'relative flex h-full min-w-[100px] flex-col gap-2 border border-border',
        'ea-product-card',
        className?.main,
        '@container',
      )}
    >
      {/* {!!templateSettings.product_teaser?.favorite_icon?.enabled && (
        <div className={cn('absolute right-4 top-4 z-10', 'ea-product-card__add-to-favorites')}>
          <AddToFavoritesButton slug={product.slug} isFavorite={product.is_favorite ?? false} />
        </div>
      )} */}

      <div
        className={cn(
          'absolute right-4 top-4 z-10 flex flex-col flex-wrap items-center gap-1 text-xs',
          'ea-product-card__badges',
        )}
      >
        {product.tags?.map((badge, i) => (
          <Fragment key={`badge-${i}`}>
            <Badge
              style={{ backgroundColor: badge.color, color: getContrastYIQ(badge.color) }}
              size="sm"
              className={cn('ea-product-card__badge')}
            >
              {badge.name}
            </Badge>
          </Fragment>
        ))}
      </div>

      <CardContent
        className={cn(
          'flex flex-1 flex-col gap-2 p-1 pb-0 @[200px]:p-3',
          'ea-product-card__content',
        )}
      >
        {imageUrl && (
          <Link prefetchOnHover href={productUrl ?? '#'} className="" prefetch={false}>
            <Image
              alt={resolveImageAlt(image, product?.name)}
              src={imageUrl}
              width={600}
              height={600}
              sizes={buildImageSizes([
                { min: 'xl', size: '600px' },
                { min: 'md', size: '33vw' },
                { size: '50vw' },
              ])}
              className="aspect-square w-full rounded-lg object-contain"
              loading="lazy"
            />
          </Link>
        )}

        <Link
          prefetchOnHover
          href={productUrl ?? '#'}
          className={cn('text-center text-lg font-bold', 'ea-product-card__name')}
          prefetch={false}
        >
          {product.name}
        </Link>

        {!!templateSettings.product_teaser?.short_description?.enabled && (
          <HtmlRendererRaw
            className={cn(
              'line-clamp-2 overflow-hidden text-ellipsis text-center text-sm font-normal text-muted-foreground',
              'ea-product-card__short-description',
            )}
            html={product.short_description}
          />
        )}
      </CardContent>

      {/* footer */}
      <CardFooter
        className={cn(
          'flex flex-col justify-end gap-2 p-1 pt-0 @container @[200px]:p-3',
          'ea-product-card__footer',
        )}
      >
        <div
          className={cn(
            'flex w-full flex-row items-center justify-between',
            'ea-product-card__footer-top',
          )}
        >
          {!!templateSettings.product_teaser?.availability?.enabled && (
            <ProductStock
              className={cn(
                'absolute left-3 whitespace-nowrap',
                'ea-product-cart__stock',
                priceDisplay.showPreviousPrice ? 'top-10' : '-top-4',
                isCistiarne && '-top-4',
              )}
            />
          )}
          {/* {product.video && (
            <Button
              variant="link"
              className={cn(
                'flex h-auto flex-row items-center space-x-2 px-0 py-0 text-destructive underline',
                'ea-product-card__video-button',
              )}
              // onClick={() => handleVideoClick(product.video ?? '')}
            >
              <FaYoutube className={cn('h-4 w-4', 'ea-product-card__video-icon')} />
              <span
                className={cn(
                  'text-xs font-medium text-destructive',
                  'ea-product-card__video-text',
                )}
              >
                Video
              </span>
            </Button>
          )} */}
        </div>

        {/* Price */}
        {/* <Price product={product} filterVariants={filterVariants} /> */}
        {product?.categories &&
        product?.categories[0]?.slug === 'realizacia-na-kluc' ? null : askForPrice ? null : (
          <ProductPrice
            product={product}
            className={{
              main: cn('items-center', 'ea-product-card__product-price'),
              discountWrapper: 'absolute left-3 top-4',
              priceAfterDiscount: 'text-[18px]',
            }}
          />
        )}

        <div className="flex w-full gap-2">
          {product?.categories && product?.categories[0]?.slug === 'realizacia-na-kluc' ? (
            <ResponsiveDialogProvider>
              <PriceCalculatorButton
                label={t('text2278')}
                className={{ box: 'w-full', button: 'w-full' }}
              />{' '}
            </ResponsiveDialogProvider>
          ) : (
            <>
              {isCistiarne || askForPrice ? (
                <div className={cn('mt-3 w-full', 'ea-product-card__ask-for-price')}>
                  <Button className="w-full" onClick={() => setIsOpenAskForPriceDialog(true)}>
                    {t('text2278')}
                  </Button>
                  <AskForPriceDialog
                    isOpen={isOpenAskForPriceDialog}
                    setIsOpen={setIsOpenAskForPriceDialog}
                    product={product}
                  />
                </div>
              ) : (
                <>
                  {!!templateSettings.product_teaser?.cart_button?.enabled && (
                    <AddToCartButton
                      product={product}
                      showIcon={!!templateSettings.product_teaser?.cart_icon}
                      sellable={isSellable(product, 1)}
                      quantity={1}
                      className={cn('mt-3 flex-grow', 'ea-product-card__add-to-cart-button')}
                      // variant="accent"
                    />
                  )}
                </>
              )}
            </>
          )}

          {!!templateSettings.product_teaser?.favorite_icon?.enabled && (
            <div
              className={cn(
                'hidden items-center justify-center @[200px]:flex',
                'ea-product-card__add-to-favorites',
              )}
            >
              <AddToFavoritesButton
                slug={product.slug}
                isFavorite={product.is_favorite ?? false}
                buttonClassName="min-w-12 max-w-12 min-h-12 max-h-12 mt-3"
              />
            </div>
          )}
        </div>

        {!!templateSettings.product_teaser?.detail_button?.enabled && (
          <Button
            asChild
            variant="secondary"
            className={cn('w-full', 'ea-product-card__detail-button')}
          >
            <Link prefetchOnHover href={productUrl ?? '#'}>
              {t('text533')}
            </Link>
          </Button>
        )}
      </CardFooter>
    </Card>
  );
}

export function ProductCard(props: ProductCardProps) {
  return (
    <VariantsProvider key={`product-${props.product.id}`} state={{ product: props.product }}>
      <DialogProvider>
        <ProductCardInternal {...props} />
      </DialogProvider>
    </VariantsProvider>
  );
}
